/**
 * Organization Service - API Client for Organizations & Members Management
 * Provides typed HTTP client for multi-tenancy operations
 */
import api from '@/api/client';
// ============================================================================
// Organization Service
// ============================================================================
class OrganizationService {
    // --------------------------------------------------------------------------
    // Organizations
    // --------------------------------------------------------------------------
    /**
     * List all organizations the user belongs to
     */
    async listOrganizations() {
        const response = await api.get('/organizations');
        return response.data;
    }
    /**
     * Get organization by ID
     */
    async getOrganization(orgId) {
        const response = await api.get(`/organizations/${orgId}`);
        return response.data;
    }
    /**
     * Create a new team organization
     */
    async createOrganization(params) {
        const response = await api.post('/organizations', params);
        return response.data;
    }
    /**
     * Update organization settings
     */
    async updateOrganization(orgId, params) {
        const response = await api.patch(`/organizations/${orgId}`, params);
        return response.data;
    }
    /**
     * Delete organization (soft delete)
     */
    async deleteOrganization(orgId) {
        await api.delete(`/organizations/${orgId}`);
    }
    // --------------------------------------------------------------------------
    // Members
    // --------------------------------------------------------------------------
    /**
     * List members of an organization
     */
    async listMembers(orgId) {
        const response = await api.get(`/organizations/${orgId}/members`);
        return response.data;
    }
    /**
     * Invite a new member to the organization
     * Backend: POST /api/organizations/:id/invite
     */
    async inviteMember(orgId, params) {
        const response = await api.post(`/organizations/${orgId}/invite`, params);
        return response.data;
    }
    /**
     * Accept an invitation token
     * Backend: POST /api/invitations/:token/accept
     */
    async acceptInvitation(token) {
        const response = await api.post(`/invitations/${token}/accept`);
        return response.data;
    }
    /**
     * Remove a member from the organization
     */
    async removeMember(orgId, userId) {
        await api.delete(`/organizations/${orgId}/members/${userId}`);
    }
    /**
     * Update member role
     */
    async updateMemberRole(orgId, userId, role) {
        const response = await api.patch(`/organizations/${orgId}/members/${userId}/role`, { role });
        return response.data;
    }
    /**
     * List pending invitations
     */
    async listInvitations(orgId) {
        const response = await api.get(`/organizations/${orgId}/invitations`);
        return response.data;
    }
    /**
     * Revoke an invitation
     * Backend: DELETE /api/invitations/:id
     */
    async revokeInvitation(orgId, tokenId) {
        await api.delete(`/invitations/${tokenId}`);
    }
    // --------------------------------------------------------------------------
    // Permissions (Resource-level, not org-level)
    // --------------------------------------------------------------------------
    // NOTE: Backend permissions API uses /api/permissions/:resourceType/:resourceId
    // Not organization-scoped like /api/organizations/:id/permissions
    // These methods are commented out until we align frontend/backend API design
    /**
     * Grant permission to user on resource
     * Backend: POST /api/permissions/grant
     */
    async grantPermission(params) {
        const response = await api.post('/permissions/grant', params);
        return response.data;
    }
    /**
     * Revoke permission from user on resource
     * Backend: POST /api/permissions/revoke
     */
    async revokePermission(params) {
        await api.post('/permissions/revoke', params);
    }
    /**
     * Get all permissions for a resource
     * Backend: GET /api/permissions/:resourceType/:resourceId
     */
    async getResourcePermissions(resourceType, resourceId) {
        const response = await api.get(`/permissions/${resourceType}/${resourceId}`);
        return response.data;
    }
    // ==========================================================================
    // Projects Management
    // ==========================================================================
    async assignProjectToOrganization(organizationId, projectId) {
        const response = await api.patch(`/organizations/${organizationId}/projects/${projectId}`);
        return response.data;
    }
    async listOrganizationProjects(organizationId) {
        const response = await api.get(`/organizations/${organizationId}/projects`);
        return response.data;
    }
}
// Export singleton instance
export const organizationService = new OrganizationService();
export default organizationService;
